import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MonthlyResult } from '../../../../models/analysis/solar-calculator-result';
import { ChartConfiguration, ChartType } from 'chart.js';
import { elEnergyChartData, elEnergyChartOptions } from '../../chart-options';
import { BaseResultChartComponent } from '../base-result-chart-component.directive';
import { forkJoin, Observable } from 'rxjs';
import { mockConsumption, mockProduction } from './mockMonthlyData';

@Component({
  selector: 'psc-monthly-chart',
  templateUrl: './monthly-chart.component.html',
})
export class MonthlyChartComponent extends BaseResultChartComponent implements OnInit {

  @Output() monthChanged: EventEmitter<number> = new EventEmitter<number>();


  mockData: boolean;
  selectedMonth: number = 0;
  currentMonthlyResult: MonthlyResult;
  allMonthlyResults = Array.from({ length: 12 }, () => elEnergyChartData({
    length: 24,
    labelMapFn: (_, i) => i + ' h',
  }));

  protected readonly elEnergyChartData = elEnergyChartData({ length: 24, labelMapFn: (_, i) => i + ' h' });
  protected readonly elEnergyChartOptions: ChartConfiguration['options'] = elEnergyChartOptions();
  protected readonly elEnergyChartType: ChartType = 'line';

  override ngOnInit() {
    super.ngOnInit();
    /**
     * t(calculator_result.chart.consumption, calculator_result.chart.production)
     */
    this.subscribeToLabelTranslation('consumption', this.elEnergyChartData.datasets[0]);
    this.subscribeToLabelTranslation('production', this.elEnergyChartData.datasets[1]);

    for (let i = 0; i < 12; i++) {
      const monthlyResultChart = this.allMonthlyResults[i];
      const monthlyResult = this.solarCalculatorResult.monthlyResult[i];
      this.subscribeToLabelTranslation('consumption', monthlyResultChart.datasets[0]);
      this.subscribeToLabelTranslation('production', monthlyResultChart.datasets[1]);
      monthlyResultChart.datasets[0].data = monthlyResult.hourlyConsumption;
      monthlyResultChart.datasets[1].data = monthlyResult.hourlyProduction;
    }

    this.subs.sink = this.resultChartsService.generateChartImageRequest.monthlyGraph.subscribe(
      () => {
        // For every chart we create an observable that will return image blob of the chart in the toBlob callback
        const observables: Observable<Blob>[] = this.charts.toArray().slice(1).map(
          (hiddenChart) => {
            return new Observable(observer => {
              hiddenChart?.chart?.canvas.toBlob(blob => {
                observer.next(blob as Blob);
                observer.complete();
              }, 'image/png');
            });
          }) as Observable<Blob>[];

        forkJoin(observables)
          .subscribe((blobs) => {
            this.resultChartsService.chartImageGenerated.monthlyGraph.next(blobs);
          });
      },
    );
  }

  protected override onInputDataChange() {
    if (this.elEnergyChartOptions?.scales?.['y']) {
      this.elEnergyChartOptions.scales['y']['suggestedMax'] = this.findMaxChartValue();
    }
  }

  private findMaxChartValue(): number {
    let maxValue = 0;
    for (const monthlyResult of this.calculatorResultValue.monthlyResult) {
      if (!monthlyResult.hourlyProduction || !monthlyResult.hourlyConsumption) {
        continue;
      }
      for (const production of monthlyResult.hourlyProduction) {
        if (production > maxValue) {
          maxValue = production;
        }
      }
      for (const consumption of monthlyResult.hourlyConsumption) {
        if (consumption > maxValue) {
          maxValue = consumption;
        }
      }
    }
    return maxValue;
  }

  onMonthChange(selectedMonth: number) {
    this.selectedMonth = selectedMonth;
    this.monthChanged.emit(this.selectedMonth);
    this.setChartData();
    this.updateCharts();
  }


  setChartData() {
    const monthlyResult = this.solarCalculatorResult.monthlyResult[this.selectedMonth];
    if (!monthlyResult) {
      return;
    }
    this.currentMonthlyResult = monthlyResult;
    this.mockData = !monthlyResult.hourlyConsumption && !monthlyResult.hourlyProduction;
    this.elEnergyChartData.datasets[0].data = monthlyResult.hourlyConsumption || mockConsumption;
    this.elEnergyChartData.datasets[1].data = monthlyResult.hourlyProduction || mockProduction;
  }
}
